import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { gatedRobotsMeta } from "@/lib/seo-gating";
import { ArrowLeft, ChevronRight, CheckCircle2, Circle } from "lucide-react";
import { type CbtSkill } from "@/data/learn/cbt-toolkit";
import { useCbtProgress } from "@/hooks/useCbtProgress";

export const Route = createFileRoute("/learn/cbt/$module/")({
  loader: async ({ params }) => {
    const { findModule } = await import("@/data/learn/cbt-toolkit");
    const module = findModule(params.module);
    if (!module) throw notFound();
    return { module };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Module not found — CBT" }, { name: "robots", content: "noindex" }] };
    }
    const m = loaderData.module;
    const title = `${m.label} — CBT Skills`;
    const url = `https://recoverytrauma.com/learn/cbt/${params.module}`;
    return {
      meta: [
        { title: `${title} · Recovery Trauma™` },
        { name: "description", content: `${m.tagline}. ${m.intro}` },
        { property: "og:title", content: title },
        { property: "og:description", content: m.intro },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        ...gatedRobotsMeta(`/learn/cbt/${params.module}`),
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: ModulePage,
  notFoundComponent: ModuleNotFound,
  errorComponent: ({ error }) => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <p className="text-sm text-rose-600">Something went wrong loading this module.</p>
      <p className="mt-2 text-xs text-muted-foreground">
        {error instanceof Error ? error.message : "Please try again."}
      </p>
      <Link to="/learn/cbt" className="mt-6 inline-block text-sm text-gold underline">Back to CBT</Link>
    </main>
  ),
});

function ModuleNotFound() {
  return (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="font-serif text-3xl text-foreground">Module not found</h1>
      <p className="mt-3 text-sm text-muted-foreground">This CBT module doesn't exist.</p>
      <Link to="/learn/cbt" className="mt-6 inline-block text-sm text-gold underline">All CBT modules</Link>
    </main>
  );
}

const THEME_BG: Record<string, string> = {
  sage: "bg-gradient-to-b from-emerald-50/60 via-background to-background",
  sky: "bg-gradient-to-b from-sky-50/60 via-background to-background",
  peach: "bg-gradient-to-b from-rose-50/60 via-background to-background",
  lavender: "bg-gradient-to-b from-violet-50/60 via-background to-background",
  gold: "bg-gradient-to-b from-amber-50/60 via-background to-background",
};

function ModulePage() {
  const { module } = Route.useLoaderData();
  const { isCompleted, moduleProgress } = useCbtProgress();
  const { done, total } = moduleProgress(module.id, module.skills.map((s: CbtSkill) => s.id));

  return (
    <main className={`min-h-dvh px-4 py-10 ${THEME_BG[module.theme] ?? ""}`}>
      <div className="mx-auto max-w-3xl">
        <Link to="/learn/cbt" className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> All CBT modules
        </Link>

        <header className="mb-8">
          <div className="text-4xl">{module.emoji}</div>
          <h1 className="mt-3 font-serif text-3xl tracking-tight text-foreground md:text-4xl">{module.label}</h1>
          <p className="mt-2 text-base font-medium text-foreground/80">{module.tagline}</p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{module.intro}</p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
            <CheckCircle2 className="h-3.5 w-3.5" /> {done} of {total} completed
          </div>
        </header>

        <ul className="space-y-3">
          {module.skills.map((s: CbtSkill, i: number) => {
            const skillDone = isCompleted(module.id, s.id);
            return (
              <li key={s.id}>
                <Link
                  to="/learn/cbt/$module/$skill"
                  params={{ module: module.id, skill: s.id }}
                  className="group flex items-center gap-4 rounded-2xl border border-border/60 bg-background p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-gold/60 hover:shadow-md"
                >
                  <div className="text-2xl">{s.emoji}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                      <h3 className="font-serif text-lg text-foreground">{s.title}</h3>
                      {skillDone && <CheckCircle2 className="h-4 w-4 text-emerald-500" aria-label="Completed" />}
                    </div>
                    <p className="mt-0.5 text-sm text-foreground/70">{s.summary}</p>
                  </div>
                  {!skillDone && <Circle className="h-4 w-4 shrink-0 text-muted-foreground/40" aria-hidden="true" />}
                  <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground transition group-hover:translate-x-1 group-hover:text-foreground" />
                </Link>
              </li>
            );
          })}
        </ul>

        <p className="mt-8 rounded-2xl border border-border bg-muted/30 p-4 text-center text-xs leading-relaxed text-muted-foreground">
          Educational only. For urgent support see{" "}
          <Link to="/crisis" className="text-gold underline-offset-2 hover:underline">Crisis Resources</Link>.
        </p>
      </div>
    </main>
  );
}
